import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SimXAgreement } from '../../../shared/models/entities/simx-agreement.model';
import { GetSimXAgreementOptions } from '../../../shared/models/requests/entity-options/get-simx-agreement-options.model';
import { AgreementDataService } from '../../../shared/services/agreement-data.service';
import { UserAgreementDataService } from '../../../shared/services/user-agreement-data.service';
import * as AuthenticationStoreActions from '../../authentication/store/authentication-store.actions';
import * as AuthenticationStoreSelectors from '../../authentication/store/authentication-store.selectors';

@Injectable({ providedIn: 'root' })
export class AgreementService {
  private readonly _actions$ = inject(Actions);
  private readonly _agreementDataService = inject(AgreementDataService);
  private readonly _store = inject(Store);
  private readonly _userAgreementDataService = inject(UserAgreementDataService);

  getAgreement(
    agreementId: string,
    options?: GetSimXAgreementOptions,
  ): Observable<SimXAgreement> {
    return this._agreementDataService.getAgreement(
      agreementId,
      options ? { returnOptions: options } : {},
    );
  }

  requestDeleteUserSignedAgreement(userAgreementId: string): void {
    this._store.dispatch(
      AuthenticationStoreActions.requestDeleteUserSignedAgreement({
        data: { userAgreementId },
      }),
    );
  }

  deleteUserSignedAgreement(userAgreementId: string) {
    return this._userAgreementDataService
      .deleteUserAgreement(userAgreementId)
      .pipe(
        map(() =>
          AuthenticationStoreActions.deleteUserSignedAgreementSucceeded({
            data: { userAgreementId },
          }),
        ),
        catchError(error =>
          of(
            AuthenticationStoreActions.deleteUserSignedAgreementFailed({
              error: error.message,
            }),
          ),
        ),
      );
  }

  getIsDeletingUserSignedAgreement$(): Observable<{
    userAgreementId: string;
  } | null> {
    return new Observable(observer => {
      const subscription = this._store
        .select(
          AuthenticationStoreSelectors.selectIsDeletingUserSignedAgreement,
        )
        .subscribe((state: { userAgreementId: string } | null) => {
          observer.next(state);
        });

      return () => subscription.unsubscribe();
    });
  }

  getDeleteUserSignedAgreementSucceeded$(): Observable<{
    userAgreementId: string;
  }> {
    return new Observable(observer => {
      const subscription = this._actions$
        .pipe(
          ofType(
            AuthenticationStoreActions.ActionType
              .DeleteUserSignedAgreementSucceeded,
          ),
        )
        .subscribe(({ data }) => {
          observer.next(data);
        });

      return () => subscription.unsubscribe();
    });
  }

  getDeleteUserSignedAgreementFailed$(): Observable<string> {
    return new Observable(observer => {
      const subscription = this._actions$
        .pipe(
          ofType(
            AuthenticationStoreActions.ActionType
              .DeleteUserSignedAgreementFailed,
          ),
        )
        .subscribe(({ error }) => {
          observer.next(error);
        });

      return () => subscription.unsubscribe();
    });
  }
}
